import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import { type FC, useId, useMemo, useState } from "react";
import { CATEGORICAL, CATEGORICAL_AUTO_SLOTS, FONT_SIZE, INK, MOTION, NEUTRAL } from "../../../theme/tokens";
import type { CategoricalColor } from "../../../utils/categoricalColors";
import { VisualChoiceGrid, type VisualChoiceOption, VisualChoiceShape } from "../Clickable/VisualChoiceGrid";
import { ColorDot } from "../molecules/ColorDot";
import { InlineHint } from "../molecules/InlineHint";
import SystemButton from "../SystemButton/SystemButton";

const AUTOMATIC = "automatic";
const AUTOMATIC_SWATCH = `conic-gradient(${CATEGORICAL.DOT.slice(0, CATEGORICAL_AUTO_SLOTS).join(", ")})`;

export type ColorPinEntry = { id: string; label: string };

type Copy = {
	title: string;
	description: string;
	automatic: string;
	automaticHint: string;
	personalHint: string;
	colorNames: readonly string[];
	pickerLabel: (label: string) => string;
	duplicate: (label: string) => string;
	restore: string;
};

type Props = {
	entries: readonly ColorPinEntry[];
	pins: Readonly<Record<string, number>>;
	resolveColor: (id: string) => CategoricalColor;
	onChange: (next: Record<string, number>, id: string, slot: number | null) => void;
	onRestore: () => void | Promise<void>;
	restoring: boolean;
	copy: Copy;
	dataTestId: string;
};

/** A reusable palette editor for any named catalog keyed by stable IDs. */
export const CategoricalColorPinsSection: FC<Props> = ({ entries, pins, resolveColor, onChange, onRestore, restoring, copy, dataTestId }) => {
	const [openId, setOpenId] = useState<string | null>(null);
	const [expanded, setExpanded] = useState(false);
	const titleId = useId();
	const contentId = useId();
	const options = useMemo<VisualChoiceOption<string>[]>(
		() => [
			{ value: AUTOMATIC, label: copy.automatic, swatch: AUTOMATIC_SWATCH, dataTestId: `${dataTestId}-option-automatic` },
			...CATEGORICAL.DOT.map((dot, slot) => ({
				value: String(slot),
				label: copy.colorNames[slot],
				swatch: dot,
				dataTestId: `${dataTestId}-option-${slot}`,
			})),
		],
		[copy.automatic, copy.colorNames, dataTestId],
	);

	const pick = (id: string, choice: string): void => {
		const next = { ...pins };
		if (choice === AUTOMATIC) delete next[id];
		else next[id] = Number(choice);
		onChange(next, id, choice === AUTOMATIC ? null : Number(choice));
	};

	if (entries.length === 0) return null;

	return (
		<Box data-testid={`${dataTestId}-section`}>
			<ButtonBase
				data-testid={`${dataTestId}-toggle`}
				aria-expanded={expanded}
				aria-controls={contentId}
				onClick={() => setExpanded((current) => !current)}
				sx={{ width: "100%", justifyContent: "flex-start", gap: 1.5, p: 1.5, borderRadius: 1, textAlign: "start" }}
			>
				<Typography id={titleId} component="span" sx={{ color: "primary.main", fontWeight: 600, fontSize: FONT_SIZE.TITLE_MD }}>
					{copy.title}
				</Typography>
				{!expanded && (
					<Box data-testid={`${dataTestId}-preview`} aria-hidden sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", flex: 1 }}>
						{entries.map((entry) => <ColorDot key={entry.id} color={resolveColor(entry.id).dot} size="sm" />)}
					</Box>
				)}
				<ExpandMoreIcon
					sx={{
						ml: "auto",
						color: "primary.main",
						transform: expanded ? "rotate(180deg)" : "none",
						transition: `transform ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { transition: "none" },
					}}
				/>
			</ButtonBase>
			<Collapse in={expanded} id={contentId}>
				<Typography sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, px: 1.5, pb: 1 }}>{copy.description}</Typography>
				<Box role="list" aria-labelledby={titleId} sx={{ ml: 1.5 }}>
					{entries.map((entry) => {
						const open = openId === entry.id;
						const pinned = pins[entry.id];
						const duplicate = pinned === undefined ? undefined : entries.find((other) => other.id !== entry.id && resolveColor(other.id).slot === pinned)?.label;
						return (
							<Box key={entry.id} role="listitem">
								<ButtonBase
									data-testid={`${dataTestId}-swatch-${entry.id}`}
									aria-expanded={open}
									onClick={() => setOpenId(open ? null : entry.id)}
									sx={{ width: "100%", justifyContent: "flex-start", gap: 1, py: 0.75, px: 1, borderRadius: 1, "@media (hover: hover) and (pointer: fine)": { "&:hover": { bgcolor: NEUTRAL[100] } } }}
								>
									<ColorDot color={resolveColor(entry.id).dot} size="md" />
									<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 500, color: INK.PRIMARY, flex: 1, textAlign: "start" }}>{entry.label}</Typography>
									<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>{pinned === undefined ? copy.automatic : copy.colorNames[pinned]}</Typography>
								</ButtonBase>
								<Collapse in={open} unmountOnExit>
									<Box sx={{ px: 1, pb: 1 }}>
										<VisualChoiceGrid ariaLabel={copy.pickerLabel(entry.label)} options={options} value={pinned === undefined ? AUTOMATIC : String(pinned)} onChange={(choice) => pick(entry.id, choice)} size="SM" shape={VisualChoiceShape.Circle} dataTestId={`${dataTestId}-picker-${entry.id}`} />
										<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, mt: 0.5 }}>{pinned === undefined ? copy.automaticHint : copy.personalHint}</Typography>
											{duplicate !== undefined && <InlineHint tone="warning" open dataTestId={`${dataTestId}-duplicate-${entry.id}`} sx={{ mt: 1 }}>{copy.duplicate(duplicate)}</InlineHint>}
									</Box>
								</Collapse>
							</Box>
						);
					})}
				</Box>
				<SystemButton data-testid={`${dataTestId}-restore`} buttonVariant="borderless" onClick={onRestore} disabled={restoring} loading={restoring} sx={{ ml: 1.5, mt: 0.5 }}>{copy.restore}</SystemButton>
			</Collapse>
		</Box>
	);
};
